'use client'

import Button from '@/components/buttons/basic';
import CheckAccess from '@/components/checkAccess';
import EditorAurora from '@/components/editoraurora';
import InputFakeField from '@/components/inputs/fake-field';
import Select from '@/components/select';
import { Permission } from '@/enums/permission-enum';
import { QueryKeys } from '@/enums/query-keys.enum';
import { EnumUserTranslationType } from '@/enums/user-translation-types.enum';
import useMessage from '@/hooks/useMessage.hook';
import usePermissions from '@/hooks/usePermissions.hook';
import useTranslation from '@/hooks/useTranslate.hook';
import { getAccountCi, getAccountUserTranslations, getCommunicationlLanguages, getMandatorDataAll, getMandatorProfilePhotoUrl, getSessionUserData, invalidateWebCacheTokens, postAccountCi, postAccountUserTranslation, postFileMandator, putAccountUserTranslation } from '@/services/api';
import { iAccountCi, iAccountUserTranslations } from '@/services/api-interfaces';
import ValidationHelper from '@/utils/validation.util';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import googleFontsData from "../../../../mocked/python/googleFontsOutput.json";
import React, { useEffect, useRef, useState } from 'react';
import SvgInvertColors from '@/components/svgs/invert-colors';
import InputBasic from '@/components/inputs/basic';
import { Pattern } from '@/enums/patterns.enum';
import style from './style.module.scss';
import SvgAccountcircle from '@/components/svgs/accountcircle';
import Divider from '@/components/divider';
import SvgSave from '@/components/svgs/save';
import ModalAttachment from '@/components/modals/attachmentmodal';
import { sortByStringProperty } from '@/utils/sort.util';

export interface MandatorDataInterface {
    name: string | null,
    companyName: string | null,
    street: string | null,
    zipCode: string | null,
    city: string | null,
    country: string | null,
    uid: string | null,
    companyDescription: string | null,
    mandatorBundleTypeId: number | null,
    mandatorAccountTypeId: number | null,
    workspaceBillable: boolean,
    billingMailAddress: string | null,
    terminationDate?: string | null,
    markForDeletion: boolean
}

const initCorportaDesignSettings: iAccountCi = {
    primaryColour: "#00d48d",
    secondaryColour: "#0058d4",
    h1FontLink: "Roboto",
    h1FontSize: "36",
    h1FontVariant: "",
    h1FontWeight: "700",
    h2FontLink: "Roboto",
    h2FontSize: "28",
    h2FontVariant: "",
    h2FontWeight: "600",
    h3FontLink: "Roboto",
    h3FontSize: "22",
    h3FontVariant: "",
    h3FontWeight: "500",
    bodyFontLink: "Open Sans",
    bodyFontSize: "16",
    bodyFontVariant: "",
    bodyFontWeight: "400",
    colourBlack: "#374151"
}

const CorporateDesign = () => {

    const { setAlert } = useMessage();
    const queryClient = useQueryClient();
    const { t, setLocale } = useTranslation();
    const { checkPermissions, checkExplicitPermissions } = usePermissions();

    const refPrimaryColor = useRef<any>(null);
    const refSecondaryColor = useRef<any>(null);
    const refBlackColor = useRef<any>(null);

    const [hasImage, setHasImage] = useState(true);
    const [formFieldsTranslate, setFormFieldsTranslate] = useState({ langId: 0 });
    const [formDesignSettings, setFormDesignSettings] = useState<iAccountCi>(initCorportaDesignSettings);
    const [isModalAttachmentOpen, setIsModalAttachmentOpen] = useState(false);
    const [detailsFormFields, setDetailsFormFields] = useState<MandatorDataInterface>({
        name: null,
        companyName: null,
        street: null,
        zipCode: null,
        city: null,
        country: null,
        uid: null,
        companyDescription: null,
        mandatorBundleTypeId: null,
        mandatorAccountTypeId: null,
        workspaceBillable: false,
        billingMailAddress: null,
        markForDeletion: false
    });

    const { data: sessionUserData, isFetched: isFetchedSessionData } = useQuery({
        queryKey: [QueryKeys.SessionUserData],
        queryFn: () => getSessionUserData(),
        gcTime: 1200000, 
        staleTime: 600000
    });

    const { data: dataCommunicationLanguages, isSuccess: isSuccessDataCommunicationLanguages } = useQuery({
        queryKey: [QueryKeys.CommunicationLanguages], 
        queryFn: () => getCommunicationlLanguages(),
        select: (data: any) => {
    
            let transformedData = data;
    
            transformedData.forEach((item: any) => {
                item['translationKey'] = item.name.replace(/[,:'.\s]/g, '').toLowerCase();
            });
    
            transformedData.forEach((item: any) => {
                item['name_translated'] = t(`languages_speaking.${item.translationKey}`);
                item['name_translated2'] = t(`languages_speaking.${item.translationKey}`);
            });
    
            transformedData.forEach((item: any) => {
                item.name_translated2 = item.name_translated2.replace(/ä/g, "ae");
                item.name_translated2 = item.name_translated2.replace(/Ä/g, "ae");
                item.name_translated2 = item.name_translated2.replace(/ö/g, "oe");
                item.name_translated2 = item.name_translated2.replace(/Ö/g, "oe");
                item.name_translated2 = item.name_translated2.replace(/ü/g, "ue");
                item.name_translated2 = item.name_translated2.replace(/Ü/g, "ue");
                item.name_translated2 = item.name_translated2.replace(/ß/g, "ss");
            });
    
            let sorted = transformedData.sort((item1: any, item2: any) => sortByStringProperty(item1, item2, 'name_translated2'));
            
            return sorted;
        }
    });
    
    const { data: dataAccountCi, isSuccess: isSuccessAccountCi } = useQuery({
        queryKey: [QueryKeys.AccountCi],
        queryFn: () => getAccountCi()
    });

    const { data: dataUserTranslation, isFetched: isFetchedDataUserTranslation, isError: isErrorDataUserTranslation } = useQuery({
        queryKey: [QueryKeys.AccountUserTranslation, { userTranslationType: EnumUserTranslationType.CompanyDescription, dataRowId: (sessionUserData?.mandatorId ?? 1), langId: formFieldsTranslate.langId }],
        queryFn: () => getAccountUserTranslations({ userTranslationType: EnumUserTranslationType.CompanyDescription, dataRowId: (sessionUserData?.mandatorId ?? 1), langId: formFieldsTranslate.langId }),
        enabled: (sessionUserData?.mandatorId ?? 1) > 0 && formFieldsTranslate.langId > 0 && !ValidationHelper.isPropertyNullOrEmpty(EnumUserTranslationType.CompanyDescription)
    });

    const { data: dataMandator, isSuccess: isSuccessDataMandator, isError: isErrorDataMandator } = useQuery({
        queryKey: [QueryKeys.MandatorDataAll],
        queryFn: () => getMandatorDataAll(sessionUserData.mandatorId),
        enabled: isFetchedSessionData
    });

    const { mutate: mutatePostAccountUserTranslation, isPending: isPendingPostAccountUserTranslation } = useMutation({ mutationFn: postAccountUserTranslation });
    const { mutate: mutatePutAccountUserTranslation, isPending: isPendingPutAccountUserTranslation } = useMutation({ mutationFn: putAccountUserTranslation });
    const { mutate: mutatePostAccountCi, isPending: isPendingPostAccounCi } = useMutation({ mutationFn: postAccountCi });
    const { mutate: mutatePostFileMandator, isPending: isLoadingMutatePostFileMandator } = useMutation({ mutationFn: postFileMandator });

    const handleTranslationSubmit = () => {

        const payload: iAccountUserTranslations = {
            dataRowId: sessionUserData?.mandatorId,
            langId: formFieldsTranslate.langId,
            translation: detailsFormFields.companyDescription ?? '',
            userTranslationType: EnumUserTranslationType.CompanyDescription
        }

        if (isErrorDataUserTranslation) {
            mutatePostAccountUserTranslation(payload, {
                onSuccess: () => {
                    setAlert({ text: t('modal_translations.messages.post.success'), type: 'success' });
                },
                onError: () => {
                    setAlert({ text: t('modal_translations.messages.post.error'), type: 'error' })
                }
            })
        }

        if (!isErrorDataUserTranslation) {
            mutatePutAccountUserTranslation(payload, {
                onSuccess: () => {
                    setAlert({ text: t('modal_translations.messages.put.success'), type: 'success' });
                },
                onError: () => {
                    setAlert({ text: t('modal_translations.messages.put.error'), type: 'error' })
                }
            })
        }
    }

    const handleSubmitCorporate = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        mutatePostAccountCi(formDesignSettings, {
            onSuccess: (data: any) => {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.success')}`, type: 'success' })
            },
            onError: () => {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.error')}`, type: 'error' })
            }
        })

        handleTranslationSubmit();
    }

    const handleAttachments = (f: any): void => {

        if (f?.length == 0) {
            setIsModalAttachmentOpen(false);
            return;
        }

        var bodyFormData = new FormData();
        bodyFormData.append('file', f[0].file);

        var payload = {
            fileType: "MandatorProfilePhoto",
            file: bodyFormData
        };

        mutatePostFileMandator(
            payload,
            {
                onSuccess: (response: any) => {
                    setAlert({ text: t('photo') + " " + t('was_updated'), type: 'success' });
                    invalidateWebCacheTokens();
                    setIsModalAttachmentOpen(false);
                    setHasImage(true);
                },
                onError: (error: any) => {
                    setAlert({ text: t('photo') + " " + t('was_not_updated'), type: 'alert' });
                }
            }
        );

    }

    const fontChangeHandler = (element: "h1" | "h2" | "h3" | "body", font: string) => {
        /* do not touch this without Amar :) */
        const fontRelatedWeights = googleFontsData.find((item: any) => item.family === font)?.variants?.map((item: any) => { return { name: item, id: item, rowData: item } });
        if (element === "h1") {
            const findWeight = fontRelatedWeights?.find((weight: any) => weight.name === formDesignSettings.h1FontWeight)?.name;
            if (ValidationHelper.isPropertyNullOrEmpty(findWeight)) {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.error_weight')}`, type: 'error' })
            }
            setFormDesignSettings({ ...formDesignSettings, h1FontLink: font, h1FontWeight: !ValidationHelper.isPropertyNullOrEmpty(findWeight) ? findWeight : fontRelatedWeights && fontRelatedWeights[0].name })
        }
        if (element === "h2") {
            const findWeight = fontRelatedWeights?.find((weight: any) => weight.name === formDesignSettings.h2FontWeight)?.name;
            if (ValidationHelper.isPropertyNullOrEmpty(findWeight)) {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.error_weight')}`, type: 'error' })
            }
            setFormDesignSettings({ ...formDesignSettings, h2FontLink: font, h2FontWeight: !ValidationHelper.isPropertyNullOrEmpty(findWeight) ? findWeight : fontRelatedWeights && fontRelatedWeights[0].name })
        }
        if (element === "h3") {
            const findWeight = fontRelatedWeights?.find((weight: any) => weight.name === formDesignSettings.h3FontWeight)?.name;
            if (ValidationHelper.isPropertyNullOrEmpty(findWeight)) {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.error_weight')}`, type: 'error' })
            }
            setFormDesignSettings({ ...formDesignSettings, h3FontLink: font, h3FontWeight: !ValidationHelper.isPropertyNullOrEmpty(findWeight) ? findWeight : fontRelatedWeights && fontRelatedWeights[0].name })
        }
        if (element === "body") {
            const findWeight = fontRelatedWeights?.find((weight: any) => weight.name === formDesignSettings.bodyFontWeight)?.name;
            if (ValidationHelper.isPropertyNullOrEmpty(findWeight)) {
                setAlert({ text: `${t('mandator_settings_page.corporate_design.messages.error_weight')}`, type: 'error' })
            }
            setFormDesignSettings({ ...formDesignSettings, bodyFontLink: font, bodyFontWeight: !ValidationHelper.isPropertyNullOrEmpty(findWeight) ? findWeight : fontRelatedWeights && fontRelatedWeights[0].name })
        }
    }

    const getFontData = (fontLink: string) => {
        const font = googleFontsData.find((item: any) => item.family === fontLink);

        if (!font) {
            return { font: null, fontVariants: [] };
        }

        const fontVariants = font.variants?.map((variant: any) => ({
            name: variant,
            id: variant,
            rowData: variant,
        }));

        return { font, fontVariants };
    };

    const fontWeights = [
        { name: "Thin", id: "100" },
        { name: "Extra Light", id: "200" },
        { name: "Light", id: "300" },
        { name: "Normal", id: "400" },
        { name: "Medium", id: "500" },
        { name: "Semi Bold", id: "600" },
        { name: "Bold", id: "700" },
        { name: "Extra Bold", id: "800" },
        { name: "Black", id: "900" },
        { name: "Extra Black", id: "950" },
    ]


    useEffect(() => {
        if (dataUserTranslation && isFetchedDataUserTranslation) {
            setDetailsFormFields((prev: any) => ({
                ...prev,
                companyDescription: dataUserTranslation?.translation
            }))
        }

        if (isErrorDataUserTranslation) {
            setDetailsFormFields((prev: any) => ({
                ...prev,
                companyDescription: ''
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchedDataUserTranslation, dataUserTranslation, isErrorDataUserTranslation]);

    useEffect(() => {
        if (!ValidationHelper.isPropertyNullOrEmpty(dataAccountCi)) {
            setFormDesignSettings(dataAccountCi);
        }
    }, [dataAccountCi]);

    useEffect(() => {
        if (isSuccessDataMandator && dataMandator) {
            setDetailsFormFields((prev: any) => ({
                ...prev,
                name: dataMandator.name,
                companyName: dataMandator.companyName,
                street: dataMandator.street,
                zipCode: dataMandator.zipCode,
                city: dataMandator.city,
                country: dataMandator.country,
                uid: dataMandator.uid,
                companyDescription: dataMandator.companyDescription,
                mandatorAccountTypeId: dataMandator.mandatorAccountTypeId,
                billingMailAddress: dataMandator.billingMailAddress,
                mandatorBundleTypeId: dataMandator.mandatorBundleTypeId,
                workspaceBillable: dataMandator.workspaceBillable,
                markForDeletion: dataMandator.markForDeletion
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessDataMandator, dataMandator]);

    useEffect(() => {
        if (sessionUserData) {
            setFormFieldsTranslate({
                langId: sessionUserData.languageId
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionUserData]);
    
    return (
        <CheckAccess permissions={[Permission.ManageWorkspaceSettings]}>
            <div className='u-grid u-gap--24'>
                {isFetchedSessionData && hasImage && (
                    <InputFakeField customClass="u-width--fit-content" label={t("mandator_create_page.workspace_image_preview")}>
                        <div className="c-applicant-details__photo-wrap u-flex u-flex--left-x">
                            <picture className={"c-applicant-details__photo--blue c-applicant-details__photo"}>
                                <div className="c-applicant-details__photo__img_wrapper" >
                                    {(
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img src={getMandatorProfilePhotoUrl(sessionUserData?.mandatorId) || "/images/lady.svg"}
                                            alt={sessionUserData?.lastname || "profile photo"}
                                            width="124"
                                            height="124"
                                            onError={() => {
                                                setHasImage(false);
                                            }}
                                        //placeholder={t("empty")}
                                        />
                                    )}
                                </div>
                            </picture>
                        </div>
                    </InputFakeField>
                )}
                <div className="c-form__item">
                    <Button text={hasImage ? t("mandator_profile_photo") : t("mandator_profile_photo_none")} variant="outlined" color="success" icon={[<SvgAccountcircle key="svg-edit" />]} onClick={() => setIsModalAttachmentOpen(true)} />
                </div>
                <form className='c-form' onSubmit={(e) => handleSubmitCorporate(e)}>
                    <div className="c-form__item">
                        <Select
                            title={t('mandator_create_page.company_description_language')}
                            placeholder={t('mandator_create_page.company_description_language')}
                            data={dataCommunicationLanguages}
                            onChange={(data: any) => setFormFieldsTranslate({ ...formFieldsTranslate, langId: data.id })}
                            defaultValue={(sessionUserData && dataCommunicationLanguages?.find((item: any) => item.id === formFieldsTranslate.langId)?.id) ?? null}
                        />
                    </div>
                    <InputFakeField
                        label={`${t("mandator_create_page.company_description")} - ${t(`languages_speaking.${(dataCommunicationLanguages?.find((item: any) => item.id === formFieldsTranslate.langId)?.name.toLowerCase()) ?? null}`)}`}
                    >
                        <EditorAurora
                            initialValue={detailsFormFields?.companyDescription || ""}
                            onChange={(value: any) => setDetailsFormFields({
                                ...detailsFormFields,
                                companyDescription: value.level.content
                            })}
                            init={{
                                height: 300,
                                width: "100%",
                                menubar: false,
                                contextmenu: false,
                                branding: false,
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } .tox-tinymce { border-radius: 4px}'
                            }}
                        />
                    </InputFakeField>
                    
                    <Divider />
                    
                    <div className="c-form__item">
                        <div ref={refBlackColor}>
                            <InputBasic 
                                label={t('mandator_settings_page.corporate_design.black')}
                                name='primaryColor'
                                type='text'
                                required
                                placeholder={`#000000`}
                                icon={<SvgInvertColors fill={formDesignSettings.colourBlack} />}
                                value={formDesignSettings.colourBlack ?? '#374151'}
                                onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, colourBlack: e.target.value })}
                                customClass={style.cColorInputField}
                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                errorMsg={t('stepstone_tab.hex_Validation_error')}
                            />
                        </div>
                    </div>
                    <div className="c-form__item">
                        <div ref={refPrimaryColor}>
                            <InputBasic 
                                label={t('mandator_settings_page.corporate_design.primary_color')}
                                name='primaryColor'
                                type='text'
                                placeholder={`#000000`}
                                icon={<SvgInvertColors fill={formDesignSettings.primaryColour} />}
                                value={formDesignSettings.primaryColour ?? ''}
                                onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, primaryColour: e.target.value })}
                                customClass={style.cColorInputField}
                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                errorMsg={t('stepstone_tab.hex_Validation_error')}
                            />
                        </div>
                    </div>
                    <div className="c-form__item">
                        <div ref={refSecondaryColor}>
                            <InputBasic 
                                label={t('mandator_settings_page.corporate_design.secondary_color')}
                                name='secondaryColor'
                                type='text'
                                placeholder={`#000000`}
                                icon={<SvgInvertColors fill={formDesignSettings.secondaryColour} />}
                                value={formDesignSettings.secondaryColour ?? ''}
                                onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, secondaryColour: e.target.value })}
                                customClass={style.cColorInputField}
                                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                                errorMsg={t('stepstone_tab.hex_Validation_error')}
                            />
                        </div>
                    </div>

                    <InputFakeField 
                        label={t('mandator_settings_page.corporate_design.header.title1')}
                        customClass={style.cCorporateDesign__fontsWrap}
                    >
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.font')}
                            placeholder={t('mandator_settings_page.corporate_design.header.font')}
                            data={googleFontsData.map((item: any) => { return { name: item.family, id: item.family, rowData: item }} )}
                            onChange={(data: any) => fontChangeHandler("h1", data.name)}
                            defaultValue={getFontData(formDesignSettings.h1FontLink)?.font?.family}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.variant')}
                            placeholder={t('mandator_settings_page.corporate_design.header.variant')}
                            data={getFontData(formDesignSettings.h1FontLink).fontVariants}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h1FontVariant: data.name })}
                            defaultValue={getFontData(formDesignSettings.h1FontLink)?.fontVariants?.find((weight: any) => weight.name === formDesignSettings.h1FontVariant)?.name}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.weight')}
                            placeholder={t('mandator_settings_page.corporate_design.header.weight')}
                            data={fontWeights}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h1FontWeight: data.id })}
                            defaultValue={formDesignSettings?.h1FontWeight}
                            errorText={"error"}
                        />
                        <InputBasic 
                            label={t('mandator_settings_page.corporate_design.header.size')}
                            name='h1Size'
                            type='number'
                            placeholder="1"
                            value={formDesignSettings.h1FontSize ?? 0}
                            onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, h1FontSize: e.target.value.toString() })}
                            customClass={style.cColorInputField}
                            pattern={Pattern.OnlyNumbers}
                            errorMsg={t('stepstone_tab.hex_Validation_error')}
                            min='1'
                            max='200'
                        />
                    </InputFakeField>

                    <InputFakeField 
                        label={t('mandator_settings_page.corporate_design.header.title2')}
                        customClass={style.cCorporateDesign__fontsWrap}
                    >
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.font')}
                            placeholder={t('mandator_settings_page.corporate_design.header.font')}
                            data={googleFontsData.map((item: any) => { return { name: item.family, id: item.family, rowData: item }} )}
                            onChange={(data: any) => fontChangeHandler("h2", data.name)}
                            defaultValue={getFontData(formDesignSettings.h2FontLink)?.font?.family}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.variant')}
                            placeholder={t('mandator_settings_page.corporate_design.header.variant')}
                            data={getFontData(formDesignSettings.h2FontLink).fontVariants}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h2FontVariant: data.name })}
                            defaultValue={getFontData(formDesignSettings.h2FontLink)?.fontVariants?.find((weight: any) => weight.name === formDesignSettings.h2FontVariant)?.name}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.weight')}
                            placeholder={t('mandator_settings_page.corporate_design.header.weight')}
                            data={fontWeights}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h2FontWeight: data.id })}
                            defaultValue={formDesignSettings?.h2FontWeight}
                            errorText={"error"}
                        />
                        <InputBasic 
                            label={t('mandator_settings_page.corporate_design.header.size')}
                            name='h2Size'
                            type='number'
                            placeholder="1"
                            value={formDesignSettings.h2FontSize ?? 0}
                            onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, h2FontSize: e.target.value.toString() })}
                            customClass={style.cColorInputField}
                            pattern={Pattern.OnlyNumbers}
                            errorMsg={t('mandator_settings_page.corporate_design.messages.error_font')}
                            min='1'
                            max='200'
                        />
                    </InputFakeField>

                    <InputFakeField 
                        label={t('mandator_settings_page.corporate_design.header.title3')}
                        customClass={style.cCorporateDesign__fontsWrap}
                    >
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.font')}
                            placeholder={t('mandator_settings_page.corporate_design.header.font')}
                            data={googleFontsData.map((item: any) => { return { name: item.family, id: item.family, rowData: item }} )}
                            onChange={(data: any) => fontChangeHandler("h3", data.name)}
                            defaultValue={getFontData(formDesignSettings.h3FontLink)?.font?.family}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.variant')}
                            placeholder={t('mandator_settings_page.corporate_design.header.variant')}
                            data={getFontData(formDesignSettings.h3FontLink).fontVariants}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h3FontVariant: data.name })}
                            defaultValue={getFontData(formDesignSettings.h3FontLink)?.fontVariants?.find((weight: any) => weight.name === formDesignSettings.h3FontVariant)?.name}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.weight')}
                            placeholder={t('mandator_settings_page.corporate_design.header.weight')}
                            data={fontWeights}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, h3FontWeight: data.id })}
                            defaultValue={formDesignSettings?.h3FontWeight}
                            errorText={"error"}
                        />
                        <InputBasic 
                            label={t('mandator_settings_page.corporate_design.header.size')}
                            name='h3Size'
                            type='number'
                            placeholder="0"
                            value={formDesignSettings.h3FontSize ?? 0}
                            onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, h3FontSize: e.target.value.toString() })}
                            customClass={style.cColorInputField}
                            pattern={Pattern.OnlyNumbers}
                            errorMsg={t('mandator_settings_page.corporate_design.messages.error_font')}
                            min='0'
                            max='200'
                        />
                    </InputFakeField>

                    <InputFakeField 
                        label={t('mandator_settings_page.corporate_design.header.titleBody')}
                        customClass={style.cCorporateDesign__fontsWrap}
                    >
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.font')}
                            placeholder={t('mandator_settings_page.corporate_design.header.font')}
                            data={googleFontsData.map((item: any) => { return { name: item.family, id: item.family, rowData: item }} )}
                            onChange={(data: any) => fontChangeHandler("body", data.name)}
                            defaultValue={getFontData(formDesignSettings.bodyFontLink)?.font?.family}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.variant')}
                            placeholder={t('mandator_settings_page.corporate_design.header.variant')}
                            data={getFontData(formDesignSettings.bodyFontLink).fontVariants}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, bodyFontVariant: data.name })}
                            defaultValue={getFontData(formDesignSettings.bodyFontLink)?.fontVariants?.find((weight: any) => weight.name === formDesignSettings.bodyFontVariant)?.name}
                            errorText={"error" }
                        />
                        <Select
                            title={t('mandator_settings_page.corporate_design.header.weight')}
                            placeholder={t('mandator_settings_page.corporate_design.header.weight')}
                            data={fontWeights}
                            onChange={(data: any) => setFormDesignSettings({ ...formDesignSettings, bodyFontWeight: data.id })}
                            defaultValue={formDesignSettings?.bodyFontWeight}
                            errorText={"error"}
                        />
                        <InputBasic 
                            label={t('mandator_settings_page.corporate_design.header.size')}
                            name='h3Size'
                            type='number'
                            placeholder="1"
                            value={formDesignSettings.bodyFontSize ?? 0}
                            onChange={(e: any) => setFormDesignSettings({ ...formDesignSettings, bodyFontSize: e.target.value.toString() })}
                            customClass={style.cColorInputField}
                            pattern={Pattern.OnlyNumbers}
                            errorMsg={t('mandator_settings_page.corporate_design.messages.error_font')}
                            min='1'
                            max='200'
                        />
                    </InputFakeField>

                    <div className="c-form__item c-form__item--align-right">
                        <Button
                            variant="contained"
                            type="submit"
                            text={t("save_button")}
                            color="success"
                            icon={[<SvgSave key="svg-save" />]}
                            disabled={checkExplicitPermissions([Permission.KarriereSupport])}
                        />
                    </div>

                </form>
                
            </div>
            
            {isModalAttachmentOpen &&
                <ModalAttachment
                    title={"Profile photo"}
                    isOpen={isModalAttachmentOpen}
                    multipleSelect={false}
                    isLoading={isLoadingMutatePostFileMandator}
                    onClose={(f: any) => { handleAttachments(f ? f : []); }}
                />
            }
        </CheckAccess>
    )
}

export default CorporateDesign
